import { z } from 'zod';
import type { JobAppLinkRoute } from '@paige/capturepoint';

/**
 * The review route's address. Every key is a hint, never a requirement: a value the route does not
 * know is dropped, so an old or foreign link still opens the workbench instead of the failure screen.
 * There is no `document`: the workbench opens on the first document of its list and takes no other
 * (CapturePoint's `openingDocumentIndex`), and nothing in Paige links to one, so a `?document=` an
 * old link carries is dropped like any other key the route does not know (gap B19, 2026-10-20).
 */
export const reviewSearchSchema = z.object({
  /**
   * Which list the workbench opened on, from the job dashboard's "Needs review" and the notice's
   * `exceptions` ("A document needs a person"). Since the review screen became one list of every
   * document (CapturePoint 5435f5aa, synced 2026-10-31) there is no list to choose: the key is still
   * read so those links stay valid, and nothing acts on it.
   */
  queue: z.enum(['review', 'all', 'exceptions']).optional().catch(undefined),
  /**
   * Open on the job's setup (the jobs home card menu's Job setup; Dustin, 2026-10-01): `1` on
   * Overview, `export` on Export (back from a storage sign-in, with `destination`'s sheet open).
   * One-shot: the route drops both once the workbench is up, so a reload opens the documents.
   * Anything else is ignored.
   */
  setup: z
    .union([z.literal(1), z.literal('export')])
    .optional()
    .catch(undefined),
  destination: z.string().uuid().optional().catch(undefined),
  /**
   * Open on a place in the job, as CapturePoint's `capturepoint://job/...` links do
   * (docs/product/app-links.md in CapturePoint; docs/engineering/lifecycle-emails.md): Learning at
   * Results, Documents on Teach, Job setup at Lookup lists or at Review thresholds. `train` is
   * CapturePoint's Learning › Train; training is hidden in Paige, so it opens Results. One-shot, like
   * `setup`: the route drops it once the workbench has it, so a reload opens the documents.
   */
  open: z.enum(['results', 'train', 'teach', 'lookups', 'thresholds']).optional().catch(undefined),
});

export type ReviewSearch = z.infer<typeof reviewSearchSchema>;

const places = {
  results: 'job/learning/results',
  train: 'job/learning/results',
  teach: 'job/documents/teach',
  lookups: 'job/lookups',
  thresholds: 'job/thresholds',
} as const satisfies Record<NonNullable<ReviewSearch['open']>, JobAppLinkRoute>;

/** The workbench's link for `?open=`: the place in the job, or none. */
export function placeFor(open: ReviewSearch['open']): JobAppLinkRoute | undefined {
  return open ? places[open] : undefined;
}
